// Тред чата: создание по первому вводу (с прогревом процесса агента), отправка (с вложениями), стоп.
import { toast } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { api } from '../api';
import { useThreadStream } from '../streams';
import type { AttachmentRef } from './attach';
import { chatStore, type PageContext } from './store';
import type { ThreadMeta } from './useChatModel';

export function useChatThread(
  threadId: string | null,
  onCreated: (id: string) => void,
  context: PageContext | null,
) {
  const stream = useThreadStream(threadId);
  const qc = useQueryClient();
  const creating = useRef<Promise<string> | null>(null);

  /** Создать тред заранее — процесс агента поднимется, пока владелец печатает. */
  const ensure = useCallback(async (): Promise<string> => {
    if (threadId) return threadId;
    if (!creating.current) {
      const choice = chatStore.get().draftChoice;
      creating.current = api<ThreadMeta>('/api/threads', {
        context,
        warm: true,
        ...(choice ? { choice } : {}),
      })
        .then((r) => {
          // Модель нового чата известна сразу — без мигания кнопки выбора
          qc.setQueryData(['thread', r.id], r);
          chatStore.setDraftChoice(null);
          onCreated(r.id);
          void qc.invalidateQueries({ queryKey: ['threads'] });
          return r.id;
        })
        .finally(() => {
          creating.current = null;
        });
    }
    return creating.current;
  }, [threadId, context, onCreated, qc]);

  /** Отправить сообщение (с вложениями); false — не ушло, ошибка уже показана. */
  const send = useCallback(
    async (text: string, attachments: AttachmentRef[] = []): Promise<boolean> => {
      try {
        const id = await ensure();
        const r = await api<{ rework?: number }>(`/api/threads/${id}/messages`, {
          text,
          ...(attachments.length ? { attachments } : {}),
        });
        // Окно правки платформы: слова владельца ушли агенту-разработчику как доработка
        if (r.rework) {
          toast(`Агент продолжит правку #${r.rework} с учётом вашего сообщения`);
          void qc.invalidateQueries({ queryKey: ['dev'] });
        }
        void qc.invalidateQueries({ queryKey: ['threads'] });
        return true;
      } catch (e) {
        toast((e as Error).message);
        return false;
      }
    },
    [ensure, qc],
  );

  const stop = useCallback(async () => {
    if (!threadId) return;
    try {
      await api(`/api/threads/${threadId}/stop`, {});
    } catch (e) {
      toast((e as Error).message);
    }
  }, [threadId]);

  return { ...stream, send, stop, ensure };
}
